import { useCallback, useRef } from 'react';
import { classifyWithContext } from '../utils/phonicsClassifier';
import type { LetterErrorContext } from '../utils/phonicsClassifier';
import { detectProsodyErrors } from '../utils/prosodyErrorClassifier';
import type { LetterPickerErrorData } from '../components/learning/LetterPicker';

export interface PhonicsSessionError {
  categoryKey: string;
  isDigraph: boolean;
  /** Number of times this confusion appeared in the session */
  count: number;
  /** Most recent expected/attempted pair for display */
  expected: string;
  attempted: string;
}

export interface ProsodySessionError {
  type: string;
  word: string;
  count: number;
}

/** Minimum occurrences required to show guidance (confidence threshold) */
const MIN_OCCURRENCES = 2;

/**
 * Hook to accumulate phonics errors across an entire learning session.
 * Only surfaces patterns that appear 2+ times (high confidence).
 */
export function usePhonicsGuidance() {
  const countsRef = useRef(new Map<string, { count: number; isDigraph: boolean; expected: string; attempted: string }>());
  const prosodyRef = useRef(new Map<string, { type: string; word: string; count: number }>());

  /** Called on each wrong letter tap from LetterPicker.onLetterError */
  const handleLetterError = useCallback((expected: string, attempted: string, context: LetterErrorContext) => {
    const result = classifyWithContext(expected, attempted, context);
    if (!result) return;

    const existing = countsRef.current.get(result.key);
    if (existing) {
      existing.count++;
      existing.expected = expected;
      existing.attempted = attempted;
    } else {
      countsRef.current.set(result.key, {
        count: 1,
        isDigraph: result.isDigraph,
        expected,
        attempted,
      });
    }
  }, []);

  /** Called after each sentence completes — detects prosody-related listening errors */
  const handleSentenceComplete = useCallback((expectedText: string, errorData: LetterPickerErrorData) => {
    const prosodyErrors = detectProsodyErrors(expectedText, errorData);
    for (const pe of prosodyErrors) {
      const key = `${pe.type}:${pe.word.toLowerCase()}`;
      const existing = prosodyRef.current.get(key);
      if (existing) {
        existing.count++;
      } else {
        prosodyRef.current.set(key, { type: pe.type, word: pe.word, count: 1 });
      }
    }
  }, []);

  /** Get high-confidence errors (2+ occurrences), sorted by count descending */
  const getSessionErrors = useCallback((): PhonicsSessionError[] => {
    const results: PhonicsSessionError[] = [];
    countsRef.current.forEach((val, key) => {
      if (val.count >= MIN_OCCURRENCES) {
        results.push({ categoryKey: key, ...val });
      }
    });
    return results.sort((a, b) => b.count - a.count);
  }, []);

  /** Get prosody listening errors (1+ occurrence — lower threshold since these are sentence-level) */
  const getProsodyErrors = useCallback((): ProsodySessionError[] => {
    return Array.from(prosodyRef.current.values())
      .filter(e => e.count >= 1)
      .sort((a, b) => b.count - a.count);
  }, []);

  /** Reset for new session */
  const resetSession = useCallback(() => {
    countsRef.current = new Map();
    prosodyRef.current = new Map();
  }, []);

  return { handleLetterError, handleSentenceComplete, getSessionErrors, getProsodyErrors, resetSession };
}
